import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  Checkbox,
  Divider,
  Flex,
  Heading,
  Link,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  ParticipationRequestStatus,
  useSubmitParticipationRequestMutation,
  type ParticipationRequestDto,
  type ParticipationRequestPatchDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { StepNavigation } from "../../../../common/StepNavigation";
import type { WizardStepPath } from "../../../../common/wizard/wizardStepPaths";
import { useApiError } from "../../../../common/errors/useApiError";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { composeCriterionKey as criterionKey } from "../../../../common/qualification/criterionKey";
import { emptyEspd } from "../bid/espd/useEspdAnswers";
import { isCriterionComplete, isEconomicOperatorComplete } from "../bid/espd/espdCompleteness";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";

interface Props {
  eventId: string;
  requestId: string;
  // Query-fresh request: the submitted state must reflect the refetch after the submit
  // mutation, which the editor coordinator's captured state never does.
  request: ParticipationRequestDto;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
}

export const RequestReviewStep = ({ eventId, requestId, request, event, previousStep }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const apiError = useApiError();
  const portalUrls = usePortalEventUrls();
  const requestSteps = portalUrls.request.edit.go(eventId, requestId);
  const { state, updateField, flushPending, fieldStatus } = useEditor<
    ParticipationRequestDto,
    ParticipationRequestPatchDto
  >(requestId);
  const [submitRequest, { isLoading: isSubmitting }] = useSubmitParticipationRequestMutation();

  const espd = state.espdResponse ?? emptyEspd();
  const finalDeclarationConfirmed = espd.finalDeclaration?.confirmed ?? false;
  const isSavingDeclaration =
    fieldStatus("espdResponse") === "saving" || fieldStatus("espdResponse") === "pending";

  const supplierCriteria = event.applicableSupplierDeclarationCriteria ?? [];
  // The server also requires the operator's own details, so they count as one more part of the
  // section — otherwise a competition with no declaration criteria has nothing to point at.
  const supplierTotal = supplierCriteria.length + 1;
  const supplierAnswered =
    supplierCriteria.filter((criterion) => isCriterionComplete(criterion, espd)).length +
    (isEconomicOperatorComplete(espd) ? 1 : 0);
  const exclusionCriteria = event.applicableExclusionCriteria ?? [];
  const exclusionAnswered = exclusionCriteria.filter((criterion) => isCriterionComplete(criterion, espd)).length;
  const qualificationCriteria = event.selectionCriteria ?? [];
  const qualificationAnswered = qualificationCriteria.filter((criterion) => {
    const response = (state.qualificationResponses ?? []).find(
      (r) => r.criterionKey === criterionKey(criterion),
    );
    return (response?.description ?? "").trim().length > 0;
  }).length;

  const setFinalDeclaration = (confirmed: boolean) =>
    updateField({ espdResponse: { ...espd, finalDeclaration: { confirmed } } }, { debounceMs: 300 });

  const [submitFailed, setSubmitFailed] = useState(false);

  const submit = async () => {
    setSubmitFailed(false);
    try {
      await flushPending();
      await submitRequest({ requestId }).unwrap();
      window.scrollTo(0, 0);
    } catch (e) {
      setSubmitFailed(true);
      apiError.trigger(e);
      window.scrollTo(0, 0);
    }
  };

  if (request.status !== ParticipationRequestStatus.Draft) {
    return (
      <Box pb="8" pt="6">
        <Alert status="success" rounded="xl" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">{t("Your request to participate has been submitted")}</Text>
            <Text fontSize="sm" color="smMuted">
              {request.submittedAt
                ? `${t("Submitted")} ${displayDateWithTime(request.submittedAt)}. `
                : null}
              {t(
                "The buyer reviews all requests after the deadline. Qualified suppliers get the invitation to tender with the bid deadline.",
              )}
            </Text>
            <Link
              as={RouterLink}
              to={portalUrls.view.go(eventId)}
              color="smPrimary"
              fontSize="sm"
              fontWeight="semibold"
              display="inline-block"
              mt="2"
            >
              {t("Back to the procurement")}
            </Link>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Mirrors the server-side completeness gate: submission is one-shot, so an incomplete
  // request must never reach the BFF only to bounce with a field-less 400.
  const readyToSubmit =
    finalDeclarationConfirmed &&
    supplierAnswered === supplierTotal &&
    exclusionAnswered === exclusionCriteria.length &&
    qualificationAnswered === qualificationCriteria.length;

  return (
    <Box pb="8" pt="6">
      <Heading size="md" mb="2">
        {t("Summary and submission")}
      </Heading>

      {submitFailed && (
        <Alert status="error" rounded="md" mb="4" alignItems="flex-start">
          <AlertIcon />
          <Text fontSize="sm">
            {t("The request was not submitted. Check the sections below and try again.")}
          </Text>
        </Alert>
      )}
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("Review the request before submitting. It can not be changed after submission.")}
      </Text>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Stack spacing="3" divider={<Divider borderColor="smBorder" />}>
            <SummaryRow
              label={t("About the supplier")}
              answered={supplierAnswered}
              total={supplierTotal}
              stepPath={requestSteps.supplier}
            />
            {exclusionCriteria.length > 0 && (
              <SummaryRow
                label={t("Grounds for exclusion")}
                answered={exclusionAnswered}
                total={exclusionCriteria.length}
                stepPath={requestSteps.exclusion}
              />
            )}
            {qualificationCriteria.length > 0 && (
              <SummaryRow
                label={t("Qualification requirements")}
                answered={qualificationAnswered}
                total={qualificationCriteria.length}
                stepPath={requestSteps.qualification}
              />
            )}
          </Stack>
        </CardBody>
      </Card>

      <Box
        mb="6"
        borderWidth="1px"
        borderRadius="md"
        p="3"
        borderColor={finalDeclarationConfirmed ? "smSuccessSoftBorder" : "smBorder"}
        bg={finalDeclarationConfirmed ? "smSuccessSoft" : "smBackground"}
      >
        <Flex align="center" gap="3">
          <Checkbox
            colorScheme="teal"
            isChecked={finalDeclarationConfirmed}
            onChange={(e) => setFinalDeclaration(e.target.checked)}
            sx={{ ".chakra-checkbox__label": { opacity: 1 } }}
          >
            <Text fontSize="sm">
              {t(
                "I declare that the information in this self-declaration and the answers above are correct and complete.",
              )}
            </Text>
          </Checkbox>
          {isSavingDeclaration && <Spinner size="sm" color="smMuted" />}
        </Flex>
      </Box>

      <StepNavigation
        onPrevious={previousStep}
        onNext={submit}
        nextLabel={t("Submit request to participate")}
        nextIsAction
        isNextLoading={isSubmitting}
        isNextDisabled={!readyToSubmit}
      />
    </Box>
  );
};

const SummaryRow = ({
  label,
  answered,
  total,
  stepPath,
}: {
  label: string;
  answered: number;
  total: number;
  stepPath: WizardStepPath;
}) => {
  const { t } = useTranslation();
  const complete = answered === total;
  return (
    <Flex justify="space-between" align="center" gap="3" flexWrap="wrap">
      <Text fontSize="sm" fontWeight="semibold">
        {label}
      </Text>
      <Flex align="center" gap="3">
        <Text fontSize="sm" fontWeight="semibold" color={complete ? "smSuccess" : "smWarning"}>
          {t("{{answered}} of {{total}} answered", { answered, total })}
        </Text>
        {!complete && (
          <Link as={RouterLink} to={stepPath} color="smPrimary" fontSize="sm" fontWeight="semibold">
            {t("Complete it")}
          </Link>
        )}
      </Flex>
    </Flex>
  );
};
